import { authToken, graphqlRequest } from "./client";
import type { Track, UserRole } from "./types";

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  participantId?: string | null;
  role: UserRole;
  track?: Track | null;
  /** Admins only: their password was set by someone else and must be replaced before anything else. */
  mustChangePassword?: boolean;
  /** Participants only: a brand-new account must take the walkthrough once before reaching its steps. */
  tourRequired?: boolean;
  /** When the walkthrough was first finished (ISO date), or null. */
  tourDoneAt?: string | null;
}

const USER_FIELDS =
  "id email firstName lastName participantId role track mustChangePassword tourRequired tourDoneAt";

interface SignInInput {
  email: string;
  participantId: string;
  firstName: string;
  lastName: string;
}

/** Participant sign-in with program email and GATEWAY ID. Stores the session token. */
export async function participantSignIn(input: SignInInput): Promise<SessionUser> {
  const data = await graphqlRequest<{
    participantSignIn: { sessionToken: string; user: SessionUser };
  }>(
    `mutation($input: ParticipantSignInInput!) { participantSignIn(input: $input) { sessionToken user { ${USER_FIELDS} } } }`,
    { variables: { input }, authenticated: false },
  );
  authToken.set(data.participantSignIn.sessionToken);
  return data.participantSignIn.user;
}

/** Admin sign-in with email and password. Stores the session token. */
export async function adminLogin(email: string, password: string): Promise<SessionUser> {
  const data = await graphqlRequest<{ adminLogin: { sessionToken: string; user: SessionUser } }>(
    `mutation($input: AdminLoginInput!) { adminLogin(input: $input) { sessionToken user { ${USER_FIELDS} } } }`,
    { variables: { input: { email, password } }, authenticated: false },
  );
  authToken.set(data.adminLogin.sessionToken);
  return data.adminLogin.user;
}

/** The signed-in user. */
export async function fetchMe(): Promise<SessionUser> {
  return (await graphqlRequest<{ me: SessionUser }>(`{ me { ${USER_FIELDS} } }`)).me;
}

/** Exchange a recently expired token for a new one. */
export async function refreshSession(): Promise<void> {
  const data = await graphqlRequest<{ refreshSession: string }>(`mutation { refreshSession }`);
  authToken.set(data.refreshSession);
}

/** Choose the skill track. It can only be set once. */
export async function pickTrack(track: Track): Promise<SessionUser> {
  return (
    await graphqlRequest<{ pickTrack: SessionUser }>(
      `mutation($track: Track!) { pickTrack(track: $track) { ${USER_FIELDS} } }`,
      { variables: { track } },
    )
  ).pickTrack;
}

/** Record that the participant finished the walkthrough. Only the first finish is kept; replays change nothing. */
export async function completeWalkthrough(): Promise<SessionUser> {
  return (
    await graphqlRequest<{ completeWalkthrough: SessionUser }>(
      `mutation { completeWalkthrough { ${USER_FIELDS} } }`,
    )
  ).completeWalkthrough;
}

/** Change your own admin password. Other sessions are signed out; this one gets a fresh token. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<SessionUser> {
  const data = await graphqlRequest<{
    changePassword: { sessionToken: string; user: SessionUser };
  }>(
    `mutation($input: ChangePasswordInput!) { changePassword(input: $input) { sessionToken user { ${USER_FIELDS} } } }`,
    { variables: { input: { currentPassword, newPassword } } },
  );
  authToken.set(data.changePassword.sessionToken);
  return data.changePassword.user;
}

/** Email a reset link. Always succeeds, so it never reveals whether the email is an admin. */
export async function requestPasswordReset(email: string): Promise<void> {
  await graphqlRequest<{ requestPasswordReset: boolean }>(
    `mutation($email: String!) { requestPasswordReset(email: $email) }`,
    { variables: { email }, authenticated: false },
  );
}

/** Set a new password from the emailed link; signs the admin in. */
export async function resetPassword(token: string, newPassword: string): Promise<SessionUser> {
  const data = await graphqlRequest<{
    resetPassword: { sessionToken: string; user: SessionUser };
  }>(
    `mutation($input: ResetPasswordInput!) { resetPassword(input: $input) { sessionToken user { ${USER_FIELDS} } } }`,
    { variables: { input: { token, newPassword } }, authenticated: false },
  );
  authToken.set(data.resetPassword.sessionToken);
  return data.resetPassword.user;
}

export function signOut(): void {
  authToken.clear();
}
